/**
 * src/state/pendingRecordWrites.js
 *
 * "Is any per-record write still in flight for this tenant?" — asked by
 * usePersistence's background refresh before it replaces `state` (GH #5174,
 * #5241).
 *
 * The problem this closes
 * ----------------------
 * `usePersistence.refresh()` already refuses to overwrite local state while a
 * BLOB write is pending: it checks `localDirtyRef`, `pendingState`,
 * `inFlightSaveRef` — and, since ADR-0001, `hasPendingRiskWrites()`, because
 * risk edits leave the blob's world entirely and the blob's own flags cannot
 * see them.
 *
 * Every other per-record write path was added after that guard and never
 * joined it. `findings` (findings-write), `soaData` (controls-write), and the
 * supplier family all debounce their durable write OUTSIDE the blob save, so
 * there is a window — after the blob save has settled and cleared
 * `localDirtyRef`, before the per-record write has landed — in which the guard
 * believes nothing is in flight. A refresh arriving in that window (the 60 s
 * poll, a visibility change when the user alt-tabs back, or a realtime
 * `state-changed` broadcast caused by a completely unrelated save in another
 * tab) replaces `state` wholesale with the server's copy, and the user's
 * in-progress edit is gone from the screen with nothing to show for it.
 *
 * That is what #5174 reports as "opening the same record in a second tab wipes
 * the first tab's in-progress edit form", and what #5241's BCDR case reports
 * as "an unrelated record's save discarded this edit". Neither needs a
 * concurrency-grain collision to happen — the two tabs in the BCDR case
 * touched different records, and the second tab's save only mattered because
 * it triggered a broadcast that made the first tab refresh over itself.
 *
 * Why entries expire
 * ------------------
 * A guard that can be held open forever is worse than the bug it fixes: a
 * caller that registers and then throws before resolving would silently
 * disable background refresh for the rest of the session, and the user would
 * stop seeing anyone else's changes with no indication why. Registrations
 * therefore carry a deadline and stop counting once it passes. The window is
 * generous relative to any debounce in the app but far short of the 60 s poll,
 * so at worst a leak costs one skipped refresh.
 */

// How long a registration can hold the refresh guard open. Comfortably longer
// than the longest durable-write debounce in the app (controls-write's 800 ms,
// findings' own timer) plus a slow round-trip, and comfortably shorter than
// the 60 s background poll.
const MAX_HOLD_MS = 15_000;

/** key -> expiry timestamp (ms). */
const pending = new Map();

function sweep() {
  if (pending.size === 0) return;
  const now = Date.now();
  for (const [key, expiresAt] of pending) {
    if (expiresAt <= now) pending.delete(key);
  }
}

/**
 * Declare that a durable per-record write for `key` is pending, so a
 * background refresh must not overwrite local state until it settles.
 *
 * Idempotent per key — re-registering extends the hold, which is what a
 * keystroke-driven debounce wants.
 *
 * @param {string} key  stable per-record key, e.g. `finding:<id>`
 * @param {number} [holdMs]  override the default hold window
 */
export function registerPendingRecordWrite(key, holdMs = MAX_HOLD_MS) {
  if (!key) return;
  pending.set(key, Date.now() + Math.max(0, holdMs));
}

/** The write landed (or failed terminally) — stop holding the guard open. */
export function resolvePendingRecordWrite(key) {
  if (!key) return;
  pending.delete(key);
}

/** True while any per-record write is pending and un-expired. */
export function hasPendingRecordWrites() {
  sweep();
  return pending.size > 0;
}

/** Keys still holding the guard open — for diagnostics. */
export function getPendingRecordWriteKeys() {
  sweep();
  return [...pending.keys()];
}

/** Test hook. */
export function resetPendingRecordWrites() {
  pending.clear();
}
